import React, { useState } from 'react';
import { 
  X, 
  ExternalLink, 
  CheckCircle, 
  ArrowRight, 
  AlertCircle,
  Key,
  Shield,
  Settings,
  CreditCard,
  Smartphone,
  Monitor
} from 'lucide-react';

const KisApiGuideModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);

  if (!isOpen) return null;

  const steps = [
    {
      title: "한국투자증권 홈페이지 접속",
      description: "KIS API 키를 발급받기 위해 공식 홈페이지에 접속합니다",
      icon: Monitor
    },
    {
      title: "계좌 개설 또는 로그인",
      description: "투자 계좌가 없으면 먼저 계좌를 개설해야 합니다",
      icon: CreditCard
    },
    {
      title: "API 서비스 신청",
      description: "트레이딩 API 사용을 위한 서비스를 신청합니다",
      icon: Key
    },
    {
      title: "APP KEY & SECRET 발급",
      description: "최종적으로 API 키와 시크릿 키를 발급받습니다",
      icon: Shield
    }
  ];

  const StepContent = ({ step }) => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 h-80 overflow-y-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Monitor className="h-6 w-6 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">1단계: 한국투자증권 홈페이지 접속</h3>
                  <p className="text-blue-800 mt-1">API 키 발급을 위해 공식 홈페이지에 접속해야 합니다.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="font-medium">한국투자증권 공식 홈페이지</p>
                  <p className="text-sm text-gray-600">https://www.kiscom.co.kr</p>
                </div>
                <a
                  href="https://www.kiscom.co.kr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <span>방문하기</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
                
              </div>
              <div>
                  <img src ="/images/1단계_로그인.png" alt="로그인" className="h-30 w-50 object-contain"/>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-yellow-800">
                    <p className="font-medium">주의사항</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• 반드시 공식 홈페이지를 이용하세요</li>
                      <li>• 피싱 사이트에 주의하세요</li>
                      <li>• 보안 프로그램 설치가 필요할 수 있습니다</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 h-80 overflow-y-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CreditCard className="h-6 w-6 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900">2단계: 계좌 개설 또는 로그인</h3>
                  <p className="text-green-800 mt-1">한국투자증권 계좌가 있어야 API를 사용할 수 있습니다.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">기존 고객</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>아이디/비밀번호로 로그인</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>공동인증서 또는 간편인증</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>바로 API 신청 가능</span>
                  </li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">신규 고객</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    <span>온라인 계좌 개설 필요</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    <span>신분증 및 서류 준비</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    <span>계좌 개설 후 API 신청</span>
                  </li>
                </ul>
              </div>
            </div>
            <div>
                  <img src ="/images/2단계_메뉴위치및신청방법.png" alt="로그인" className="h-30 w-50 object-contain"/>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">필요한 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                <ul className="space-y-1">
                  <li>• 계좌번호 (8자리-2자리)</li>
                  <li>• 계좌 비밀번호</li>
                  <li>• 공동인증서 또는 간편인증</li>
                </ul>
                <ul className="space-y-1">
                  <li>• 본인 명의 휴대폰</li>
                  <li>• 이메일 주소</li>
                  <li>• 투자자 정보</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 h-80 overflow-y-auto">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Key className="h-6 w-6 text-purple-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-purple-900">3단계: API 서비스 신청</h3>
                  <p className="text-purple-800 mt-1">개발자 센터에서 API 사용 권한을 신청합니다.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">API 신청 경로</h4>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <span className="bg-gray-100 px-2 py-1 rounded">로그인</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="bg-gray-100 px-2 py-1 rounded">트레이딩</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="bg-gray-100 px-2 py-1 rounded">Open API</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="bg-blue-100 px-2 py-1 rounded text-blue-700">API 신청</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">신청 정보 입력</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>앱 이름: AI Trading System</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>사용 목적: 개인 자동매매</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>서비스 유형: 실전투자</span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">승인 과정</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>승인까지 1-3일 소요</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>이메일로 승인 결과 통보</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>승인 후 키 발급 가능</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                  <img src ="/images/3단계_키획득단계.png" alt="로그인" className="h-30 w-50 object-contain"/>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="text-orange-800">
                    <p className="font-medium">모의투자 vs 실전투자</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• <strong>모의투자</strong>: 가상 돈으로 연습, 즉시 승인</li>
                      <li>• <strong>실전투자</strong>: 실제 돈으로 거래, 승인 심사 필요</li>
                      <li>• 처음에는 모의투자로 테스트 추천</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-6 w-6 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900">4단계: APP KEY & SECRET 발급</h3>
                  <p className="text-green-800 mt-1">승인 완료 후 실제 API 키를 발급받습니다.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">키 발급 위치</h4>
                <div className="flex items-center space-x-2 text-sm text-gray-700 mb-4">
                  <span className="bg-gray-100 px-2 py-1 rounded">Open API</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="bg-gray-100 px-2 py-1 rounded">API 관리</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="bg-blue-100 px-2 py-1 rounded text-blue-700">키 발급/조회</span>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">발급받을 정보</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-blue-600">APP KEY</p>
                      <p className="text-gray-600">예: PSdh8qdx2e...</p>
                      <p className="text-xs text-gray-500 mt-1">앱을 식별하는 공개 키</p>
                    </div>
                    <div>
                      <p className="font-medium text-red-600">APP SECRET</p>
                      <p className="text-gray-600">예: xJKd9fG2...</p>
                      <p className="text-xs text-gray-500 mt-1">인증을 위한 비밀 키</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">보안 주의사항</h4>
                  <ul className="space-y-1 text-sm text-red-800">
                    <li>• APP SECRET은 절대 공유 금지</li>
                    <li>• 코드에 하드코딩 금지</li>
                    <li>• 정기적으로 키 재발급</li>
                    <li>• 의심스러운 활동 시 즉시 키 폐기</li>
                  </ul>
                </div>

                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">사용 제한</h4>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• 초당 20회 호출 제한</li>
                    <li>• 일일 10,000회 호출 제한</li>
                    <li>• 계좌당 동시 접속 5개 제한</li>
                    <li>• 과도한 사용 시 일시 정지</li>
                  </ul>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">설정 완료 후</h4>
                <div className="text-sm text-green-800 space-y-2">
                  <p>1. 이 화면의 설정 탭에서 APP KEY와 APP SECRET 입력</p>
                  <p>2. 계좌번호 연결 및 인증 완료</p>
                  <p>3. 자동매매 시스템 활성화</p>
                  <p>4. 테스트 거래로 정상 동작 확인</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">KIS API 키 발급 가이드</h2>
            <p className="text-gray-600 mt-1">한국투자증권 API 키를 발급받는 방법을 안내합니다</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* 진행 단계 표시 */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const stepNumber = index + 1;
              const isActive = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep;
              
              return (
                <div key={stepNumber} className="flex items-center">
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                      isActive 
                        ? 'border-blue-500 bg-blue-500 text-white' 
                        : isCompleted
                        ? 'border-green-500 bg-green-500 text-white'
                        : 'border-gray-300 bg-white text-gray-500'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <StepIcon className="h-5 w-5" />
                      )}
                    </div>
                    <div className="ml-3 hidden sm:block">
                      <p className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                        {step.title}
                      </p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-4">
                      <div className={`h-0.5 ${stepNumber < currentStep ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 콘텐츠 영역 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <StepContent step={currentStep} />
        </div>

        {/* 하단 네비게이션 */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span>이전</span>
          </button>

          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index + 1)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index + 1 === currentStep ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center space-x-3">
            {currentStep < steps.length ? (
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <span>다음</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="h-4 w-4" />
                <span>완료</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KisApiGuideModal;